import { motion } from 'framer-motion';
import { Settings, Wrench, Construction, MessageSquare, AlertTriangle } from 'lucide-react';

const DevelopmentMode = () => {
  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-[#0a0a0c] text-white selection:bg-primary-500/30">
      {/* Dynamic Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 -m-32 w-96 h-96 bg-primary-600/10 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 left-0 -m-32 w-96 h-96 bg-accent-600/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.03)_0%,transparent_70%)]" />
      </div>

      <div className="relative z-10 w-full max-w-2xl px-6 py-12 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Animated Icon Set */}
          <div className="flex justify-center items-center gap-4 text-primary-400">
             <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
             >
                <Settings size={48} className="opacity-50" />
             </motion.div>
             <motion.div 
               initial={{ y: 0 }}
               animate={{ y: [-10, 0, -10] }}
               transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
               className="bg-primary-500/10 p-5 rounded-3xl border border-primary-500/20 backdrop-blur-xl shadow-2xl shadow-primary-500/20"
             >
               <Construction size={64} className="text-primary-400" />
             </motion.div>
             <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
             >
                <Wrench size={40} className="opacity-40" />
             </motion.div>
          </div>

          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-[0.2em] bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-6 inline-block">
                 Development Mode Active
              </span>
              <h1 className="text-5xl md:text-6xl font-black tracking-tight text-white mb-6">
                Under <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-400">Construction.</span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed max-w-lg mx-auto">
                We're currently fine-tuning the PSH experience to bring you something extraordinary. We'll be back shortly.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6"
          >
            <a 
              href="https://t.me/pshsupport" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-500 text-white rounded-2xl font-bold transition-all transform hover:-translate-y-1 shadow-lg shadow-primary-600/25"
            >
              <MessageSquare size={20} />
              Telegram Support
            </a>
            <div className="flex items-center gap-2 px-6 py-4 bg-white/5 border border-white/10 rounded-2xl text-gray-300 font-medium whitespace-nowrap">
               <AlertTriangle size={18} className="text-amber-500" />
               Expected Uptime: <span className="text-white font-bold italic ml-1">Soon™</span>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-xs text-gray-600 font-medium tracking-widest uppercase pt-12"
          >
            © {new Date().getFullYear()} PSH Ecosystem • Premium Scripts Hub
          </motion.p>
        </motion.div>
      </div>

      {/* Subtle Grid Overlay */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] pointer-events-none" />
    </div>
  );
};

export default DevelopmentMode;
